/**
 * The paste dialog's reading of a review, pure: which rows still need the
 * person — no twin, or a near one ("likely") nobody has confirmed (v2,
 * 26/10/2026) — how many of each kind were found by themselves, the
 * Paste button's words, and the target group
 * opened first (the open one, else the copied group's twin by name).
 * For Media Devoted | Created By Ciro Autiero
 */
import { type GroupCopyChoices, type GroupCopyMatch, type GroupCopyReview, nameKey, prefixOf } from '@trafficpulse/shared';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** No twin, or a near one — and the person has not answered for it. */
export const needsPick = (how: GroupCopyMatch, answered: boolean) => (how === 'none' || how === 'likely') && !answered;

export type MatchKind = 'gateways' | 'lists' | 'listGroups' | 'campaigns';

type Answered = Record<string, unknown> | undefined;
const has = (map: Answered, key: string) => key in (map ?? {});

/** Each kind's rows as the summary reads them: how it was found, and whether the person answered. */
function rowsOf(review: GroupCopyReview, choices: GroupCopyChoices): Record<MatchKind, Array<{ how: GroupCopyMatch; answered: boolean }>> {
  return {
    gateways: review.gateways.map((g) => ({ how: g.how, answered: has(choices.gateways, String(g.sourceRouteId)) })),
    lists: (review.lists ?? []).map((l) => ({ how: l.how, answered: has(choices.lists, String(l.sourceWiserId)) })),
    listGroups: review.listGroups.map((l) => ({ how: l.how, answered: has(choices.listGroups, l.sourceId) })),
    campaigns: review.campaigns.map((c) => ({ how: c.how, answered: has(choices.campaigns, String(c.sourceWiserId)) })),
  };
}

export interface KindCount {
  total: number;
  /** Found by itself for sure, or answered by the person. */
  settled: number;
  needs: number;
}

export function kindCounts(review: GroupCopyReview, choices: GroupCopyChoices): Record<MatchKind, KindCount> {
  const rows = rowsOf(review, choices);
  const count = (list: (typeof rows)[MatchKind]): KindCount => ({
    total: list.length,
    settled: list.filter((r) => !needsPick(r.how, r.answered)).length,
    needs: list.filter((r) => needsPick(r.how, r.answered)).length,
  });
  return { gateways: count(rows.gateways), lists: count(rows.lists), listGroups: count(rows.listGroups), campaigns: count(rows.campaigns) };
}

/** How many rows of the review still need the person. */
export const needTotal = (review: GroupCopyReview, choices: GroupCopyChoices) =>
  Object.values(kindCounts(review, choices)).reduce((n, k) => n + k.needs, 0);

export const takenContainers = (review: GroupCopyReview) => review.containers.filter((c) => c.take).length;

export const pasteLabel = (review: GroupCopyReview) => {
  const n = takenContainers(review);
  return n === 0 ? 'Paste the master config' : `Paste ${plural(n, 'container')}`;
};

/** v2 — several countries at once: "Paste into 3 countries". */
export const pasteAllLabel = (reviews: GroupCopyReview[]) =>
  reviews.length > 1 ? `Paste into ${reviews.length} countries` : reviews[0] ? pasteLabel(reviews[0]) : 'Paste';

interface GroupLike {
  key: string;
  label: string;
}

/** A label without its leading country code ("AU_CASINO" → "casino"); a country has few groups, so each label speaks for itself. */
const bare = (label: string) => nameKey(label, prefixOf(label));

/** v2 — the group whose name is the copied one's, each without its country prefix ("AU_CASINO" ⇄ "CZ_CASINO"); null = none. */
export function twinGroup(groups: GroupLike[], copiedLabel: string): string | null {
  const key = bare(copiedLabel);
  return groups.find((g) => bare(g.label) === key)?.key ?? null;
}

/**
 * The group open on the board, else the copied group's twin by name — never another group by
 * itself (v2, 26/10/2026: it could be a group with its own master config); null = the person picks.
 */
export function defaultTarget(groups: GroupLike[], openKey: string | null, copiedLabel: string): string | null {
  return groups.find((g) => g.key === openKey)?.key ?? twinGroup(groups, copiedLabel);
}
